/** Every number and date in the application is formatted here.
 *
 * MASTER_PROMPT.md section 7.5. Indian grouping, one decimal on percentages,
 * and a real distinction between "no activity" and "measured zero" — those are
 * different facts and the UI must not blur them.
 */

const INDIAN = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

export function toNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") return NaN;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : NaN;
}

/** Indian grouping: 1,23,45,678. */
export function fmtQty(
  value: string | number | null | undefined,
  dp = 0
): string {
  const n = toNumber(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(n);
}

/** Rupees, with lakh and crore abbreviations above a lakh. */
export function fmtINR(
  value: string | number | null | undefined,
  { abbreviate = true }: { abbreviate?: boolean } = {}
): string {
  const n = toNumber(value);
  if (Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abbreviate && abs >= 1_00_00_000) {
    return `${sign}₹${(abs / 1_00_00_000).toFixed(2)}Cr`;
  }
  if (abbreviate && abs >= 1_00_000) {
    return `${sign}₹${(abs / 1_00_000).toFixed(2)}L`;
  }
  return `${sign}₹${INDIAN.format(abs)}`;
}

/** The exact figure, for a tooltip behind an abbreviated one. */
export function fmtExact(value: string | number | null | undefined): string {
  const n = toNumber(value);
  return Number.isNaN(n) ? "—" : new Intl.NumberFormat("en-IN").format(n);
}

/** Percentages to one decimal. No false precision. */
export function fmtPct(
  value: string | number | null | undefined,
  dp = 1
): string {
  const n = toNumber(value);
  return Number.isNaN(n) ? "—" : `${n.toFixed(dp)}%`;
}

/**
 * Zero is shown as an em dash when it means "no activity" and as 0 when it
 * means "measured zero". Section 7.5 — these are different facts.
 */
export function fmtQtyOrDash(
  value: string | number | null | undefined,
  { measured = false, dp = 0 }: { measured?: boolean; dp?: number } = {}
): string {
  const n = toNumber(value);
  if (Number.isNaN(n)) return "—";
  if (n === 0 && !measured) return "—";
  return fmtQty(n, dp);
}

/** DD MMM YYYY — unambiguous for Indian users. */
export function fmtDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(d);
}

export function relativeTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  const secs = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86_400) return `${Math.floor(secs / 3600)}h ago`;
  return fmtDate(d);
}

/** Shifts read as Day and Night in the UI, never A and B (section 7.5). */
export function fmtShift(shift: string | null | undefined): string {
  if (!shift) return "—";
  return shift === "DAY" ? "Day" : shift === "NIGHT" ? "Night" : shift;
}

export function fmtStatus(status: string): string {
  return status
    .toLowerCase()
    .split("_")
    .join(" ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Direction of a delta, so meaning never rides on colour alone (7.5). */
export function deltaArrow(value: number): "↑" | "↓" | "→" {
  if (value > 0) return "↑";
  if (value < 0) return "↓";
  return "→";
}

/**
 * Cost and loss metrics invert: wastage going down is good. Returns the
 * semantic token name, not a colour.
 */
export function deltaTone(
  value: number,
  { inverted = false }: { inverted?: boolean } = {}
): "positive" | "negative" | "neutral" {
  if (value === 0) return "neutral";
  const good = inverted ? value < 0 : value > 0;
  return good ? "positive" : "negative";
}

/**
 * Today, on the plant's calendar.
 *
 * `toISOString()` converts to UTC first, so in IST it returns yesterday's
 * date for the first five and a half hours of every day — a night-shift clerk
 * entering at 02:00 would have been handed the wrong business date by default.
 * A business date is a local fact; this reads the local parts directly.
 */
export function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The current month, on the plant's calendar. Same reason as `today()`:
 * `toISOString()` would return the previous month on the 1st until 05:30 IST.
 */
export function thisMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Last month — the one people actually close.
 *
 * Two traps, both of which the period-close screen fell into. `toISOString()`
 * has the timezone problem above; and `d.setMonth(d.getMonth() - 1)` on the
 * 31st of a month whose predecessor is shorter overflows — on 31 March it
 * gives 3 March, not February. Arithmetic on the year and month directly
 * avoids both, because a month has no day for the rollover to catch.
 */
export function lastMonth(): string {
  const now = new Date();
  const month = now.getMonth();               // 0-11
  const year = month === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const previous = month === 0 ? 12 : month;  // 1-12
  return `${year}-${String(previous).padStart(2, "0")}`;
}
